import styled from '@emotion/styled';
import { Theme } from '@mui/material';
import { BlobFilePreview, MediaBlobType, UUID } from '@myme/utils';
import { AudioIcon, PlayVideoPreviewIcon } from 'src/icons';
import { transientOptions } from 'src/utils';
import { SvgIcon, Tooltip, Typography } from 'src/components/atoms';

interface Props extends BlobFilePreview {
  fileUUID: UUID | null;
  onFileSelect: (e: React.MouseEvent<HTMLElement>, name: string) => void;
}

const StyledFileContainer = styled.div`
  display: grid;
  grid-template-rows: 6rem min-content;
  justify-items: center;
  box-sizing: border-box;
  cursor: pointer;
`;
interface StyledImageContainerProps {
  type: MediaBlobType;
  $isSelected: boolean;
}

const getImageContainerBorder = (isSelected: boolean, type: MediaBlobType, theme: Theme) => {
  if (isSelected) {
    return `2px solid ${theme.palette.primary.main}`;
  }

  return type === 'AUDIO' ? `1px solid ${theme.palette.grey[600]}` : 'none';
};

const StyledImageContainer = styled(
  'div',
  transientOptions
)<StyledImageContainerProps>(
  ({ theme, type, $isSelected }) => `
    position: relative;
    display: flex;
    align-items: center;
    height: 5.75rem;
    width: 5.75rem;
    background-color: ${theme.palette.secondary.main};
    border: ${getImageContainerBorder($isSelected, type, theme)};
    margin-bottom: 0.25rem;

    img, #filePreview-video {
      max-height: 5.75rem;
      max-width: ${$isSelected ? 'calc(5.75rem - 4px)' : '5.75rem'};

      display: ${type === 'AUDIO' ? 'none' : 'initial'};
    }

    &::before {
      content: '';
      z-index: 5;
      position: absolute;
      width: 100%;
      height: 100%;
      background-color: ${theme.palette.common.black};
      opacity: 0.3;
      
      display: ${type === 'VIDEO' ? 'initial' : 'none'};
    }

    svg {
      position: absolute;
      z-index: 10;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      height: 2.5rem;
      width: 2.5rem;
    }
    `
);

const StyledFileName = styled(Typography)`
  width: 6rem;
  font-size: 12px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SingleFilePreview = ({ id, fileName, fileType, fileUrl, fileUUID, onFileSelect }: Props) => {
  return (
    <StyledFileContainer onClick={e => onFileSelect(e, id)} data-testid={`Blob media file${fileName}`}>
      <StyledImageContainer $isSelected={fileUUID === id} type={fileType}>
        {fileType !== 'VIDEO' ? (
          <img src={fileUrl} alt='single file' />
        ) : (
          <video id='filePreview-video' onContextMenu={() => false} preload='metadata' controls={false}>
            <track kind='captions' />
            <source src={`${fileUrl}#t=0.001`} type='video/mp4' />
          </video>
        )}
        {fileType === 'AUDIO' && (
          <SvgIcon>
            <AudioIcon />
          </SvgIcon>
        )}
        {fileType === 'VIDEO' && (
          <SvgIcon>
            <PlayVideoPreviewIcon />
          </SvgIcon>
        )}
      </StyledImageContainer>
      <Tooltip title={fileName}>
        <StyledFileName>{fileName}</StyledFileName>
      </Tooltip>
    </StyledFileContainer>
  );
};
